import { ArrowRight, Calendar, Clock, AlertCircle, CheckCircle2, CreditCard, AlertTriangle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import PaymentHistory from './PaymentHistory';

interface SubscriptionStatusProps {
  compact?: boolean;
  /** Called when user clicks upgrade/subscribe after cancellation or expiry. */
  onOpenUpgrade?: () => void;
}

type UsageKey = 'daily' | 'weekly' | 'monthly' | 'total';

export default function SubscriptionStatus({ compact = false, onOpenUpgrade }: SubscriptionStatusProps) {
  const { subscription, usage, quotaStatus, isLoadingSubscription, error, fetchPaymentHistory, cancelSubscription } = useSubscription();

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (subscription) fetchPaymentHistory();
  }, [subscription, fetchPaymentHistory]);

  const expirationDate = subscription?.expiresAt
    ? new Date(subscription.expiresAt)
    : null;

  const isExpired = expirationDate ? expirationDate < new Date() : false;
  const isExpiredButActive = subscription?.status === 'active' && isExpired;
  const isCancelled = subscription?.cancelAtPeriodEnd && subscription?.status === 'active';

  const statusBadge = () => {
    if (subscription?.status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
          Pending
        </span>
      );
    }
    if (subscription?.status === 'active') {
      if (isExpiredButActive) {
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
            Expired
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
          <CheckCircle2 className="w-3 h-3" />
          Active
        </span>
      );
    }
    if (subscription?.status === 'expired') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          Expired
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-body">
        Free
      </span>
    );
  };

  const renderUsageBar = (key: UsageKey, label: string) => {
    const fromQuota = quotaStatus
      ? key === 'daily'
        ? { used: quotaStatus.dailyUsed, limit: quotaStatus.dailyLimit }
        : key === 'weekly'
          ? { used: quotaStatus.weeklyUsed, limit: quotaStatus.weeklyLimit }
          : key === 'monthly'
            ? { used: quotaStatus.monthlyUsed, limit: quotaStatus.monthlyLimit }
            : { used: quotaStatus.totalUsed, limit: quotaStatus.totalLimit }
      : null;
    const window = usage?.usage[key] || fromQuota || { used: 0, limit: 0 };

    if (!window) return null;
    // Skip windows that are not applicable to a plan (unbounded).
    if (window.limit >= Number.MAX_SAFE_INTEGER || window.limit <= 0) return null;

    const pct = window.limit > 0 ? Math.min(100, (window.used / window.limit) * 100) : 0;
    const barColor =
      pct >= 100
        ? 'bg-red-500'
        : pct >= 80
          ? 'bg-yellow-500'
          : 'bg-primary-500';

    if (compact) {
      return (
        <div className="flex items-center justify-between text-xs py-1">
          <span className="text-body">{label}</span>
          <span className="text-heading font-medium tabular-nums">
            {window.used}/{window.limit}
          </span>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-body">{label}</span>
          <span className="text-heading font-medium tabular-nums">
            {window.used} / {window.limit}
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div
            className={`${barColor} h-1.5 rounded-full transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    );
  };

  if (isLoadingSubscription) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        {error}
      </div>
    );
  }

  // ─── Active or expired subscription ──────────────────────────────────────

  if (subscription?.status === 'active' || subscription?.status === 'expired') {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 px-5 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium opacity-90 text-white">Current Plan</h3>
              <p className="text-xl font-bold capitalize text-white">{subscription.plan}</p>
            </div>
            {statusBadge()}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Expiration */}
          {expirationDate && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-body opacity-60 flex-shrink-0" />
              <span className="text-body">
                {isExpiredButActive || subscription.status === 'expired'
                  ? 'Expired on '
                  : 'Expires on '}
                <span className="font-medium text-heading">
                  {expirationDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </span>
            </div>
          )}

          {/* Usage bars */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-heading">Usage</p>
            {renderUsageBar('daily', 'Today')}
            {renderUsageBar('weekly', 'This Week')}
            {renderUsageBar('monthly', 'This Month')}
            {renderUsageBar('total', 'All time')}
          </div>

          {/* Payment History */}
          <PaymentHistory limit={5} />

          {/* Transaction hash */}
          {subscription.cryptoPaymentTxHash && (
            <div className="flex items-center gap-2 text-xs pt-2 border-t border-gray-100 dark:border-gray-800">
              <CreditCard className="w-3.5 h-3.5 text-body opacity-50" />
              <a
                href={`https://etherscan.io/tx/${subscription.cryptoPaymentTxHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 font-mono flex items-center gap-1"
              >
                {subscription.cryptoPaymentTxHash.slice(0, 10)}…{subscription.cryptoPaymentTxHash.slice(-8)}
                <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          )}

          {/* Cancel/expire CTAs */}
          {isCancelled && (
            <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-800">
              <p className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                This pre-paid subscription will not renew.
              </p>
              <p className="text-xs text-body">
                Your {subscription.plan} plan remains active until{' '}
                <span className="font-medium">{expirationDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.</span>
              </p>
              <p className="text-xs text-body">
                <span className="text-primary-600 dark:text-primary-400 font-medium cursor-pointer" onClick={() => onOpenUpgrade?.()}>
                  Upgrade again
                </span>
              </p>
            </div>
          )}

          {/* CTA for expired */}
          {subscription.status === 'expired' && (
            <p className="text-sm text-body pt-2 border-t border-gray-100 dark:border-gray-800">
              Your pre-paid subscription has expired.{' '}
              <span className="text-primary-600 dark:text-primary-400 font-medium cursor-pointer">
                Renew now
              </span>
            </p>
          )}

          {/* Cancel button for active subscriptions (not already cancelled) */}
          {subscription.status === 'active' && !isCancelled && (
            <button
              type="button"
              onClick={() => setShowCancelConfirm(true)}
              className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium text-left pt-2 border-t border-gray-100 dark:border-gray-800"
            >
              Cancel pre-paid subscription
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── No active subscription (free or pending) ───────────────────────────

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-body opacity-60" />
            <h3 className="text-sm font-medium text-heading">Pre-paid subscription</h3>
          </div>
          {statusBadge()}
        </div>
      </div>

      <div className="p-5 space-y-4">
        {subscription?.status === 'pending' && (
          <div className="flex items-start gap-2 text-sm bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-700 dark:text-yellow-300 font-medium">Payment pending</p>
              <p className="text-yellow-600 dark:text-yellow-400 text-xs mt-0.5">
                Your payment is being confirmed. This may take a few minutes.
              </p>
            </div>
          </div>
        )}

        {/* Free plan usage */}
        {(subscription?.plan === 'free' || !subscription?.plan) && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-heading">Free Plan Usage</p>
            {renderUsageBar('daily', 'Today')}
            {renderUsageBar('weekly', 'This Week')}
            {renderUsageBar('monthly', 'This Month')}

            {usage?.usage && Object.values(usage.usage).some((w) => w.remaining <= 5) && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 pt-1">
                Approaching your free limit.{' '}
                <span className="font-medium cursor-pointer text-primary-600 dark:text-primary-400">
                  Upgrade for more
                </span>
              </p>
            )}
          </div>
        )}

        {subscription?.plan !== 'free' && !subscription?.plan && (
          <p className="text-sm text-body">
            Connect your wallet to view your pre-paid subscription.
          </p>
        )}

        {/* Cancel confirmation modal — uses fixed positioning, renders at root level */}
        {showCancelConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowCancelConfirm(false)}>
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 max-w-sm w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-heading">Cancel pre-paid subscription?</h3>
                  <p className="text-sm text-body mt-1">
                    You will keep access to your {subscription?.plan} plan until{' '}
                    <strong>{expirationDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong>. After that date your pre-paid subscription will expire and you'll lose premium benefits. Your data will be retained.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-body bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Keep pre-paid subscription
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setCancelling(true);
                    const ok = await cancelSubscription();
                    setCancelling(false);
                    if (ok) setShowCancelConfirm(false);
                  }}
                  disabled={cancelling}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {cancelling ? (
                    <>
                      <Clock className="w-4 h-4 animate-spin" />
                      Cancelling…
                    </>
                  ) : (
                    'Yes, cancel'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}